export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly details: Record<string, unknown>;

  constructor(status: number, code: string, message: string, details: Record<string, unknown> = {}) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.code = code;
    this.details = details;
  }
}

const DEFAULT_MESSAGES: Record<number, string> = {
  400: "The request was not accepted.",
  401: "Your session has ended. Sign in again.",
  403: "You don't have access to this.",
  404: "Not found.",
  409: "Someone else changed this at the same time.",
  413: "The file is larger than 25 MB.",
  415: "This file type is not supported.",
};
const defaultMessage = (status: number) =>
  DEFAULT_MESSAGES[status] ??
  (status >= 500 ? "The server had a problem. Try again in a moment." : "The request failed.");

let unauthorized: (() => void) | null = null;
export function setUnauthorizedHandler(fn: (() => void) | null): void {
  unauthorized = fn;
}

export function csrfToken(): string | null {
  const m = /(?:^|;\s*)kd_csrf=([^;]*)/.exec(document.cookie);
  return m?.[1] ? decodeURIComponent(m[1]) : null;
}

export async function toApiError(res: Response): Promise<ApiError> {
  let code = `http_${res.status}`;
  let message = defaultMessage(res.status);
  let details: Record<string, unknown> = {};
  try {
    const body = (await res.json()) as { error?: Record<string, unknown> };
    if (body?.error && typeof body.error === "object") {
      const { code: c, message: m, ...rest } = body.error;
      if (typeof c === "string") code = c;
      if (typeof m === "string" && m) message = m;
      details = rest;
    }
  } catch {
    // not JSON: keep the default message
  }
  const retryAfter = Number(res.headers.get("Retry-After") ?? Number.NaN);
  if (Number.isFinite(retryAfter) && retryAfter >= 0) details.retryAfter = retryAfter;
  if (res.status === 401) unauthorized?.();
  return new ApiError(res.status, code, message, details);
}

export type FetchOpts = {
  method?: "GET" | "POST" | "PUT" | "PATCH" | "DELETE";
  json?: unknown;
  body?: BodyInit;
  keepalive?: boolean;
  signal?: AbortSignal;
};

export async function apiFetch(path: string, opts: FetchOpts = {}): Promise<Response> {
  const method = opts.method ?? "GET";
  const headers: Record<string, string> = { Accept: "application/json" };
  if (method !== "GET") {
    const token = csrfToken();
    if (token) headers["X-KD-CSRF"] = token;
  }
  let body = opts.body;
  if (opts.json !== undefined) {
    body = JSON.stringify(opts.json);
    headers["Content-Type"] = "application/json";
  }
  const res = await fetch(path, {
    method,
    headers,
    body,
    credentials: "same-origin",
    keepalive: opts.keepalive,
    signal: opts.signal,
  });
  if (!res.ok) throw await toApiError(res);
  return res;
}

export async function request<T>(path: string, opts: FetchOpts = {}): Promise<T> {
  const res = await apiFetch(path, opts);
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}
